import React, { useState, useEffect } from "react";
import { ReactComponent as LinkedInIcon } from "Assets/svg/linkedInOutlined.svg";
import useTeamMembersAndTeams from "../utils/useTeamMembersAndTeams";
import { CATEGORY_LABEL } from "../utils/widgetConstants";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";

const AUDIENCE_OPTIONS = [
  { id: "EVERYONE", label: "Everyone", sub: null },
  { id: "SPECIFIC_USERS", label: "Members", sub: null },
  { id: "SPECIFIC_TEAMS", label: "Teams", sub: null }
];

const ConfigureWidgetPanel = ({ widget, onBack, onConfirm, isAdding }) => {
  const [useGlobal, setUseGlobal] = useState(true);
  const [mode, setMode] = useState("EVERYONE");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedLiAccounts, setSelectedLiAccounts] = useState([]);
  const [liExpanded, setLiExpanded] = useState(false);
  const { members, teams, teamMembersMap, loading } = useTeamMembersAndTeams();

  // Same LinkedIn-account narrowing as AudienceScopePopover.js's post-add Scope popover - see that
  // file for the full rationale (independent of Everyone/Members/Teams and of useGlobal).
  const showLiAccountFilter = widget.category === "LINKEDIN";
  const isAdmin = Utils.isAdmin();
  const allLiAccounts = (isAdmin ? userDetail?.teamLinkedInAccounts : userDetail?.activeLinkedInAccounts) || [];
  let liAccounts = allLiAccounts;
  if (!useGlobal && mode === "SPECIFIC_USERS" && selectedUsers.length > 0) {
    liAccounts = allLiAccounts.filter(account => selectedUsers.includes(account?.memberId));
  } else if (!useGlobal && mode === "SPECIFIC_TEAMS" && selectedTeams.length > 0) {
    const teamMemberIds = new Set();
    selectedTeams.forEach(teamId => {
      (teamMembersMap.get(teamId) || []).forEach(id => teamMemberIds.add(id));
    });
    liAccounts = allLiAccounts.filter(account => teamMemberIds.has(account?.memberId));
  }

  const toggleUser = id =>
    setSelectedUsers(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleTeam = id =>
    setSelectedTeams(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  // Empty selectedLiAccounts renders every account as checked (all included by default). The first
  // click, starting from empty, must mean "uncheck just this one," so seed the explicit list with
  // everyone else, not with only the clicked account.
  const toggleLiAccount = id =>
    setSelectedLiAccounts(prev => {
      if (prev.length === 0) {
        return liAccounts.filter(a => a?.id !== id).map(a => a.id);
      }
      return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    });

  useEffect(() => {
    if (!selectedLiAccounts.length) return;
    const stillValid = selectedLiAccounts.filter(id => liAccounts.some(a => a?.id === id));
    if (stillValid.length !== selectedLiAccounts.length) setSelectedLiAccounts(stillValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers.join(","), selectedTeams.join(","), mode, useGlobal, liAccounts.length]);

  // Backend rejects SPECIFIC_USERS/SPECIFIC_TEAMS with an empty list (DashboardService.updateDashboard).
  // Same check as AudienceScopePopover.js's post-add Scope popover.
  const scopeInvalid =
    !useGlobal &&
    ((mode === "SPECIFIC_USERS" && selectedUsers.length === 0) ||
      (mode === "SPECIFIC_TEAMS" && selectedTeams.length === 0));

  const handleConfirm = () => {
    if (scopeInvalid) return;
    const liAccountIds = showLiAccountFilter && selectedLiAccounts.length > 0 ? selectedLiAccounts : undefined;
    if (useGlobal) {
      onConfirm(liAccountIds ? { liAccountIds } : null);
      return;
    }
    const audienceScope =
      mode === "EVERYONE"
        ? { type: "EVERYONE", memberIds: [], teamIds: [] }
        : mode === "SPECIFIC_USERS"
        ? { type: "SPECIFIC_USERS", memberIds: selectedUsers, teamIds: [] }
        : { type: "SPECIFIC_TEAMS", memberIds: [], teamIds: selectedTeams };
    if (liAccountIds) {
      audienceScope.liAccountIds = liAccountIds;
    }
    onConfirm(audienceScope);
  };

  return (
    <div className="configureWidgetPanel">
      <button type="button" className="configureWidgetBackBtn" onClick={onBack}>
        ← Back to widgets
      </button>

      <p className="configureWidgetLabel">Configure</p>
      <div className="configureWidgetTitleRow">
        <p className="configureWidgetTitle">{widget.name}</p>
        <span className="widgetCatalogCatBadge">{CATEGORY_LABEL[widget.category] || widget.category}</span>
      </div>

      <div className="configureWidgetSection">
        <div
          className={`audienceScopeGlobalRow${useGlobal ? " audienceScopeGlobalRowActive" : ""}`}
          onClick={() => setUseGlobal(v => !v)}
        >
          <span className={`audienceScopeCheckbox${useGlobal ? " audienceScopeCheckboxChecked" : ""}`} />
          <p className="audienceScopeGlobalLabel">Use the dashboard's current member/team filter</p>
        </div>

        <div className={`audienceScopeScopeBlock${useGlobal ? " audienceScopeScopeBlockDisabled" : ""}`}>
        <p className="configureWidgetSectionTitle">Show data for</p>
        <div className="audienceScopeOptions">
          {AUDIENCE_OPTIONS.map(opt => (
            <React.Fragment key={opt.id}>
              <div
                className={`audienceScopeOption${mode === opt.id ? " audienceScopeOptionActive" : ""}`}
                onClick={() => !useGlobal && setMode(opt.id)}
              >
                <span className="audienceScopeOptionRadio" />
                <div>
                  <p className="audienceScopeOptionLabel">{opt.label}</p>
                  {opt.sub && <p className="audienceScopeOptionSub">{opt.sub}</p>}
                </div>
              </div>

              {opt.id === "SPECIFIC_USERS" && mode === "SPECIFIC_USERS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading members…</p>}
                  {!loading &&
                    members.map(m => {
                      const checked = selectedUsers.includes(m.memberId);
                      return (
                        <div key={m.memberId} className="audienceScopeListRow" onClick={() => toggleUser(m.memberId)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{m.name}</span>
                        </div>
                      );
                    })}
                  {!loading && !members.length && <p className="audienceScopeEmpty">No members found</p>}
                </div>
              )}

              {opt.id === "SPECIFIC_TEAMS" && mode === "SPECIFIC_TEAMS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading teams…</p>}
                  {!loading &&
                    teams.map(t => {
                      const checked = selectedTeams.includes(t.id);
                      return (
                        <div key={t.id} className="audienceScopeListRow" onClick={() => toggleTeam(t.id)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{t.name}</span>
                        </div>
                      );
                    })}
                  {!loading && !teams.length && <p className="audienceScopeEmpty">No teams found</p>}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        </div>
      </div>

      {showLiAccountFilter && (
        <div className="audienceScopeLiSection">
          <p className="audienceScopeLiSectionNote">Applies regardless of the filter above</p>
          <div className="audienceScopeLiHeaderRow" onClick={() => setLiExpanded(v => !v)}>
            <LinkedInIcon className="audienceScopeLiIcon" />
            <span className="audienceScopeLiLabel">LinkedIn account</span>
            <span className="audienceScopeLiSummary">
              {!liAccounts.length
                ? "No accounts"
                : selectedLiAccounts.length === 0
                ? "Show all"
                : `${selectedLiAccounts.length} account${selectedLiAccounts.length === 1 ? "" : "s"}`}
            </span>
            <span className={`audienceScopeLiChevron${liExpanded ? " audienceScopeLiChevronOpen" : ""}`} />
          </div>

          {liExpanded && (
            <div className="audienceScopeList">
              {liAccounts.map(account => {
                const id = account?.id;
                const checked = selectedLiAccounts.length === 0 || selectedLiAccounts.includes(id);
                const name =
                  `${account?.firstName || ""} ${account?.lastName || ""}`.trim() || account?.email || "--";
                return (
                  <div key={id} className="audienceScopeListRow" onClick={() => toggleLiAccount(id)}>
                    <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                    <span className="audienceScopeListLabel">{name}</span>
                  </div>
                );
              })}
              {!liAccounts.length && <p className="audienceScopeEmpty">No accounts</p>}
            </div>
          )}
        </div>
      )}

      {scopeInvalid && (
        <p className="audienceScopeValidationHint">
          {mode === "SPECIFIC_USERS" ? "Select at least one member" : "Select at least one team"} to save.
        </p>
      )}

      <button
        type="button"
        className="configureWidgetConfirmBtn"
        onClick={handleConfirm}
        disabled={isAdding || scopeInvalid}
      >
        {isAdding ? "Adding…" : "Add to dashboard"}
      </button>
    </div>
  );
};

export default ConfigureWidgetPanel;
